export type OnboardingStep = "account" | "bank" | "agent" | "success" | "preparing";

export interface AccountData {
  companyName: string;
  country: string;
  currency: string;
  language: string;
  industry?: string;
}

export interface OnboardingState {
  isCompleted: boolean;
  currentStep: OnboardingStep;
  accountData?: AccountData;
  bankConnected: boolean;
  userId?: string;
}

export const SUPPORTED_COUNTRIES = [
  { code: "AT", name: "Austria", currency: "EUR", flag: "🇦🇹" },
  { code: "BE", name: "Belgium", currency: "EUR", flag: "🇧🇪" },
  { code: "BG", name: "Bulgaria", currency: "BGN", flag: "🇧🇬" },
  { code: "HR", name: "Croatia", currency: "EUR", flag: "🇭🇷" },
  { code: "CY", name: "Cyprus", currency: "EUR", flag: "🇨🇾" },
  { code: "CZ", name: "Czechia", currency: "CZK", flag: "🇨🇿" },
  { code: "DK", name: "Denmark", currency: "DKK", flag: "🇩🇰" },
  { code: "EE", name: "Estonia", currency: "EUR", flag: "🇪🇪" },
  { code: "FI", name: "Finland", currency: "EUR", flag: "🇫🇮" },
  { code: "FR", name: "France", currency: "EUR", flag: "🇫🇷" },
  { code: "DE", name: "Germany", currency: "EUR", flag: "🇩🇪" },
  { code: "GR", name: "Greece", currency: "EUR", flag: "🇬🇷" },
  { code: "HU", name: "Hungary", currency: "HUF", flag: "🇭🇺" },
  { code: "IE", name: "Ireland", currency: "EUR", flag: "🇮🇪" },
  { code: "IT", name: "Italy", currency: "EUR", flag: "🇮🇹" },
  { code: "LV", name: "Latvia", currency: "EUR", flag: "🇱🇻" },
  { code: "LT", name: "Lithuania", currency: "EUR", flag: "🇱🇹" },
  { code: "LU", name: "Luxembourg", currency: "EUR", flag: "🇱🇺" },
  { code: "MT", name: "Malta", currency: "EUR", flag: "🇲🇹" },
  { code: "NL", name: "Netherlands", currency: "EUR", flag: "🇳🇱" },
  { code: "PL", name: "Poland", currency: "PLN", flag: "🇵🇱" },
  { code: "PT", name: "Portugal", currency: "EUR", flag: "🇵🇹" },
  { code: "RO", name: "Romania", currency: "RON", flag: "🇷🇴" },
  { code: "SK", name: "Slovakia", currency: "EUR", flag: "🇸🇰" },
  { code: "SI", name: "Slovenia", currency: "EUR", flag: "🇸🇮" },
  { code: "ES", name: "Spain", currency: "EUR", flag: "🇪🇸" },
  { code: "SE", name: "Sweden", currency: "SEK", flag: "🇸🇪" },
];

export const SUPPORTED_LANGUAGES = [
  { code: "da", name: "Dansk" },
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
  { code: "et", name: "Eesti" },
  { code: "fi", name: "Suomi" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
  { code: "nl", name: "Nederlands" },
  { code: "pt", name: "Português" },
  { code: "sv", name: "Svenska" },
];

export function detectBrowserLanguage(): string {
  const browserLang = navigator.language.split("-")[0];
  const supported = SUPPORTED_LANGUAGES.find((lang) => lang.code === browserLang);
  return supported ? supported.code : "en";
}

export const INDUSTRY_KEYS = [
  "consulting",
  "construction",
  "ecommerce",
  "education",
  "finance",
  "food",
  "healthcare",
  "manufacturing",
  "marketing",
  "media",
  "real_estate",
  "technology",
  "transportation",
  "other",
] as const;

export type IndustryKey = typeof INDUSTRY_KEYS[number];
